import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { playerAPI } from '../../services/api';
import { isPlayerUiLocale } from '../../utils/playerUiLanguage';

const LangSelect = styled.select`
  appearance: none;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23043663' d='M3 4.5l3 3 3-3'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  border: 2px solid #043663;
  border-radius: 8px;
  color: #043663;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  padding: 9px 30px 9px 12px;
  min-width: 4.25rem;
  flex-shrink: 0;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #032a4a;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.25);
  }

  &:disabled {
    opacity: 0.55;
    cursor: wait;
  }

  @media (max-width: 1150px) and (min-width: 769px) {
    padding: 8px 28px 8px 10px;
    font-size: 0.75rem;
  }

  @media (max-width: 768px) {
    width: ${(p) => (p.$compact ? 'auto' : '100%')};
    max-width: ${(p) => (p.$compact ? '4.75rem' : '200px')};
    ${(p) =>
      p.$compact
        ? 'padding: 7px 24px 7px 8px; background-position: right 6px center;'
        : ''}
  }
`;

export default function LanguageSwitcher({ disabled, compact = false }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const raw = (i18n.language || 'en').split('-')[0];
  const active = isPlayerUiLocale(raw) ? raw : 'en';
  const [pending, setPending] = React.useState(false);

  const switchTo = async (code) => {
    if (!isPlayerUiLocale(code) || code === active || pending || disabled) return;
    setPending(true);
    try {
      await i18n.changeLanguage(code);
      if (user?.type === 'player') {
        await playerAPI.updateProfile({ uiLocale: code });
      }
    } catch {
      /* keep UI language if API fails; i18n may still have switched */
    } finally {
      setPending(false);
    }
  };

  return (
    <LangSelect
      $compact={compact}
      aria-label={t('header.languageChoiceAria')}
      disabled={pending || disabled}
      value={active}
      onChange={(e) => switchTo(e.target.value)}
    >
      <option value="en" lang="en">
        {t('header.languageEnglish')}
      </option>
      <option value="fr" lang="fr">
        {t('header.languageFrench')}
      </option>
      <option value="de" lang="de">
        {t('header.languageGerman')}
      </option>
      <option value="it" lang="it">
        {t('header.languageItalian')}
      </option>
      <option value="es" lang="es">
        {t('header.languageSpanish')}
      </option>
    </LangSelect>
  );
}
